import { useState, type FormEvent } from "react";
import { Building2, Plus, Users } from "lucide-react";
import { actions, listBudgets, listBudgetAlerts, listBusinessUnits, listLocations, listTeam } from "@/lib/api";
import { displayDate, money, titleCase } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { useData, matches, type DialogProps, type ViewProps } from "@/lib/app-view";
import { PageHeader } from "@/components/page-header";
import { Panel } from "@/components/panel";
import { Loading, ErrorState } from "@/components/empty-state";
import { Table } from "@/components/data-table";
import { FieldError } from "@/components/field-error";

export function Budgets({ organisation, refresh, query, can, changed }: ViewProps) {
  const [
    { data: units, error, loading },
    { data: budgets },
    { data: alerts },
    { data: locations },
    { data: team },
  ] = [
    useData(
      () => listBusinessUnits(organisation.id),
      [organisation.id, refresh],
    ),
    useData(() => listBudgets(organisation.id), [organisation.id, refresh]),
    useData(
      () => listBudgetAlerts(organisation.id),
      [organisation.id, refresh],
    ),
    useData(() => listLocations(organisation.id), [organisation.id, refresh]),
    useData(() => listTeam(organisation.id), [organisation.id, refresh]),
  ];
  const [unitOpen, setUnitOpen] = useState(false);
  const [budgetOpen, setBudgetOpen] = useState(false);
  const [ownerOpen, setOwnerOpen] = useState(false);
  if (loading) return <Loading />;
  if (error || !units) return <ErrorState message={error} />;
  const filteredBudgets = (budgets || []).filter((row) =>
    matches(
      query,
      row.cost_category,
      row.fiscal_year,
      row.business_units?.name,
    ),
  );
  return (
    <>
      <PageHeader
        eyebrow="FINANCIAL CONTROL"
        title="Structure, budgets & accountability"
        description="Model branches, departments, production units and franchises; warn owners early or hard-stop overruns."
      >
        {can("manage") && (
          <Button className="min-h-11" variant="outline" onClick={() => setUnitOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Operating unit
          </Button>
        )}
        {can("manage") && (
          <Button className="min-h-11" variant="outline" onClick={() => setOwnerOpen(true)}>
            <Users className="size-4" aria-hidden="true" />
            Assign owner
          </Button>
        )}
        {can("finance") && (
          <Button className="min-h-11" onClick={() => setBudgetOpen(true)}>
            <Plus className="size-4" aria-hidden="true" />
            Allocate budget
          </Button>
        )}
      </PageHeader>
      <section className="grid gap-4 lg:grid-cols-[.8fr_1.2fr]">
        <Panel>
          <h2 className="text-xl font-semibold">Operating hierarchy</h2>
          <div className="mt-3 grid gap-2">
            {units.map((unit) => (
              <div
                key={unit.id}
                className="flex items-center gap-3 rounded-xl border border-[var(--border)] p-3"
              >
                <span className="grid size-11 shrink-0 place-items-center rounded-lg bg-[var(--action-soft)]">
                  <Building2 className="size-4 text-[var(--action)]" aria-hidden="true" />
                </span>
                <div>
                  <strong className="text-sm">
                    {unit.code} · {unit.name}
                  </strong>
                  <small className="block text-[var(--text-secondary)]">
                    {titleCase(unit.unit_type)}
                    {unit.locations?.name ? ` · ${unit.locations.name}` : ""}
                  </small>
                </div>
              </div>
            ))}
            {!units.length && (
              <p className="text-sm text-[var(--text-secondary)]">
                Create corporate, branch, department, production or franchise
                units.
              </p>
            )}
          </div>
        </Panel>
        <Panel>
          <h2 className="text-xl font-semibold">Budget position</h2>
          <div className="mt-3 grid gap-3">
            {filteredBudgets.map((budget) => {
              const used = (budget.budget_commitments || [])
                .filter((c) => c.stage !== "released")
                .reduce((sum, c) => sum + Number(c.amount), 0);
              const percent = budget.allocated_amount
                ? Math.min(100, (used / Number(budget.allocated_amount)) * 100)
                : 100;
              return (
                <div
                  key={budget.id}
                  className="rounded-xl border border-[var(--border)] p-4"
                >
                  <div className="flex flex-col justify-between gap-3 sm:flex-row">
                    <div>
                      <strong className="text-sm">
                        {budget.business_units?.name} · {budget.cost_category}
                      </strong>
                      <small className="block text-[var(--text-secondary)]">
                        FY {budget.fiscal_year} · warning at{" "}
                        {budget.warning_percent}% ·{" "}
                        {budget.hard_stop ? "hard stop" : "warning only"}
                      </small>
                    </div>
                    <strong className="text-sm tabular-nums sm:text-right">
                      {money(used, budget.currency)} /{" "}
                      {money(budget.allocated_amount, budget.currency)}
                    </strong>
                  </div>
                  <div
                    className="mt-3 h-2 overflow-hidden rounded-full bg-[var(--surface-secondary)]"
                    role="progressbar"
                    aria-label={`${budget.cost_category} budget consumed`}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-valuenow={Math.round(percent)}
                  >
                    <div
                      className={`h-full ${percent >= 100 ? "bg-[var(--error)]" : percent >= budget.warning_percent ? "bg-[var(--warning)]" : "bg-[var(--success)]"}`}
                      style={{ width: `${percent}%` }}
                    />
                  </div>
                </div>
              );
            })}
            {!filteredBudgets.length && (
              <p className="text-sm text-[var(--text-secondary)]">
                No active budget allocations.
              </p>
            )}
          </div>
        </Panel>
      </section>
      {(alerts || []).length > 0 && (
        <section className="mt-5">
          <h2 className="mb-3 text-2xl font-semibold">Budget warnings</h2>
          <div className="hidden md:block">
            <Table
              heads={[
                "When",
                "Requisition",
                "Responsibility",
                "Severity",
                "Message",
              ]}
            >
              {(alerts || []).map((alert) => (
                <tr key={alert.id}>
                  <td>{displayDate(alert.created_at, true)}</td>
                  <td>
                    {alert.requisitions?.request_no}
                    <small className="block text-[var(--text-secondary)]">
                      {alert.requisitions?.title}
                    </small>
                  </td>
                  <td>{titleCase(alert.responsibility)}</td>
                  <td>
                    <Badge
                      value={
                        alert.severity === "hard_stop" ? "exception" : "submitted"
                      }
                    />
                  </td>
                  <td>{alert.message}</td>
                </tr>
              ))}
            </Table>
          </div>
          <div className="grid gap-3 md:hidden" aria-label="Budget warnings">
            {(alerts || []).map((alert) => (
              <article
                key={alert.id}
                className="rounded-xl border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-xs font-bold text-[var(--text-muted)]">
                      {alert.requisitions?.request_no}
                    </p>
                    <h3 className="mt-1 font-semibold">{alert.requisitions?.title}</h3>
                  </div>
                  <Badge value={alert.severity === "hard_stop" ? "exception" : "submitted"} />
                </div>
                <p className="mt-3 text-sm">{alert.message}</p>
                <div className="mt-3 flex flex-wrap justify-between gap-2 text-xs text-[var(--text-secondary)]">
                  <span>{titleCase(alert.responsibility)}</span>
                  <span className="tabular-nums">{displayDate(alert.created_at, true)}</span>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}
      <BusinessUnitDialog
        open={unitOpen}
        onOpenChange={setUnitOpen}
        organisationId={organisation.id}
        units={units}
        locations={locations || []}
        onDone={changed}
      />
      <BudgetDialog
        open={budgetOpen}
        onOpenChange={setBudgetOpen}
        organisationId={organisation.id}
        units={units}
        onDone={changed}
      />
      <ResponsibilityDialog
        open={ownerOpen}
        onOpenChange={setOwnerOpen}
        organisationId={organisation.id}
        units={units}
        team={team || []}
        onDone={changed}
      />
    </>
  );
}

function BusinessUnitDialog({
  open,
  onOpenChange,
  organisationId,
  units,
  locations,
  onDone,
}: DialogProps & {
  units: Awaited<ReturnType<typeof listBusinessUnits>>;
  locations: Awaited<ReturnType<typeof listLocations>>;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.createBusinessUnit(organisationId, {
        p_parent_id: form.get("parent") || null,
        p_location_id: form.get("location") || null,
        p_code: form.get("code"),
        p_name: form.get("name"),
        p_unit_type: form.get("type"),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="ORGANISATION DESIGN"
          title="Add operating unit"
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Code
              <Input name="code" required />
            </Label>
            <Label>
              Name
              <Input name="name" required />
            </Label>
            <Label>
              Type
              <Select name="type">
                <option value="corporate">Corporate</option>
                <option value="branch">Branch</option>
                <option value="department">Department</option>
                <option value="production_unit">Production unit</option>
                <option value="franchise">Franchise</option>
              </Select>
            </Label>
            <Label>
              Parent
              <Select name="parent">
                <option value="">Top level</option>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.code} · {unit.name}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Physical location
              <Select name="location">
                <option value="">Not location-bound</option>
                {locations.map((location) => (
                  <option key={location.id} value={location.id}>
                    {location.name}
                  </option>
                ))}
              </Select>
            </Label>
          </div>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy} type="submit">
              Create operating unit
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function BudgetDialog({
  open,
  onOpenChange,
  organisationId,
  units,
  onDone,
}: DialogProps & { units: Awaited<ReturnType<typeof listBusinessUnits>> }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.createBudget(organisationId, {
        p_business_unit_id: form.get("unit"),
        p_fiscal_year: form.get("year"),
        p_cost_category: form.get("category"),
        p_currency: String(form.get("currency")).toUpperCase(),
        p_allocated_amount: Number(form.get("amount")),
        p_warning_percent: Number(form.get("warning")),
        p_hard_stop: Boolean(form.get("hardStop")),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader eyebrow="FINANCIAL CONTROL" title="Allocate budget" />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <div className="grid gap-4 md:grid-cols-2">
            <Label>
              Operating unit
              <Select name="unit" required>
                {units.map((unit) => (
                  <option key={unit.id} value={unit.id}>
                    {unit.code} · {unit.name}
                  </option>
                ))}
              </Select>
            </Label>
            <Label>
              Financial year
              <Input
                name="year"
                placeholder="2026-27"
                pattern="[0-9]{4}-[0-9]{2}"
                required
              />
            </Label>
            <Label>
              Cost category
              <Input name="category" placeholder="Lab consumables" required />
            </Label>
            <Label>
              Currency
              <Input
                name="currency"
                defaultValue="INR"
                pattern="[A-Za-z]{3}"
                maxLength={3}
                required
              />
            </Label>
            <Label>
              Allocation
              <Input name="amount" type="number" min="0" step="0.01" required />
            </Label>
            <Label>
              Warning threshold (%)
              <Input
                name="warning"
                type="number"
                min="1"
                max="100"
                defaultValue="80"
                required
              />
            </Label>
          </div>
          <label className="flex items-center gap-3 text-sm">
            <input name="hardStop" type="checkbox" />
            Block submission when the allocation would be exceeded
          </label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy || !units.length} type="submit">
              Activate budget
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ResponsibilityDialog({
  open,
  onOpenChange,
  organisationId,
  units,
  team,
  onDone,
}: DialogProps & {
  units: Awaited<ReturnType<typeof listBusinessUnits>>;
  team: Awaited<ReturnType<typeof listTeam>>;
}) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setBusy(true);
    setError("");
    try {
      await actions.assignResponsibility(organisationId, {
        p_business_unit_id: form.get("unit") || null,
        p_user_id: form.get("user"),
        p_responsibility: form.get("responsibility"),
      });
      onOpenChange(false);
      onDone();
    } catch (reason) {
      setError((reason as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader
          eyebrow="ACCOUNTABILITY"
          title="Assign responsible owner"
        />
        <form onSubmit={submit} className="grid gap-4 p-6">
          <Label>
            Scope
            <Select name="unit">
              <option value="">Organisation-wide</option>
              {units.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.code} · {unit.name}
                </option>
              ))}
            </Select>
          </Label>
          <Label>
            Person
            <Select name="user" required>
              {team
                .filter((member) => member.status === "active")
                .map((member) => (
                  <option key={member.user_id} value={member.user_id}>
                    {member.profiles?.full_name} · {member.profiles?.email}
                  </option>
                ))}
            </Select>
          </Label>
          <Label>
            Responsibility
            <Select name="responsibility">
              <option value="department_head">Department head</option>
              <option value="production_head">Production head</option>
              <option value="procurement_head">Procurement head</option>
              <option value="accounts_head">Accounts head</option>
              <option value="management">Management</option>
            </Select>
          </Label>
          <FieldError>{error}</FieldError>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button disabled={busy || !team.length} type="submit">
              Assign responsibility
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
